import type { JSX, ReactNode } from 'react'

import { Flex, Notice } from '@buffer-mono/popcorn'

import { getMaxImageCountExceededMessage } from '../../lib/validation/utils/validationErrorMessages'
import type { Draft } from '../../entities/Draft'
import type { ValidationFeedback } from '../../stores/types'
import styles from '../css/Composer.module.css'

export const getImageLimitNoticeMessage = (
  draft: Draft,
  composerFeedbackMessages: readonly ValidationFeedback[],
): string | null => {
  // Only surface this when the draft is genuinely OVER the image limit. At the
  // limit the draft is still valid and the disabled add-media control already
  // explains the cap, so a warning here reads as something needing to be fixed
  // when nothing is. The over-limit validator runs for updates but not drafts,
  // so an over-limit draft would otherwise surface nothing — this catches it.
  // Skip it when the feed already carries the image message (an over-limit
  // update) so we never show it twice. Dedup on the message text rather than
  // the VALIDATION_CODE: MAX_LIMIT_REACHED is shared across distinct limits
  // (image count, hashtags, mentions, user tags), so a code match would wrongly
  // suppress the image notice when a coexisting limit is also exceeded.
  const maxAttachableImages = draft.service.maxAttachableImagesCount(draft)
  const isOverImageLimit =
    draft.hasImagesAttachment() && draft.images.length > maxAttachableImages
  const imageLimitText = getMaxImageCountExceededMessage(
    maxAttachableImages,
    draft.updateType,
  )
  return isOverImageLimit &&
    !composerFeedbackMessages.some((what) => what.message === imageLimitText)
    ? imageLimitText
    : null
}

export const EditorFeedbackNotices = ({
  draftId,
  composerFeedbackMessages,
  imageLimitMessage,
  renderMessageSuffix,
}: {
  draftId: string
  composerFeedbackMessages: readonly ValidationFeedback[]
  imageLimitMessage: string | null
  renderMessageSuffix?: (what: ValidationFeedback, index: number) => ReactNode
}): JSX.Element => (
  <Flex direction="column" gap="2xs" className={styles.editorNoticeWrapper}>
    {composerFeedbackMessages.map((what, index) => (
      <Notice
        variant="warning"
        key={`${draftId}-${what.message}`}
        className={styles.editorNotice}
      >
        {what.message.endsWith('.') ? what.message : `${what.message}.`}{' '}
        {renderMessageSuffix?.(what, index)}
      </Notice>
    ))}
    {imageLimitMessage && (
      <Notice
        variant="warning"
        key={`${draftId}-image-limit`}
        className={styles.editorNotice}
      >
        {`${imageLimitMessage}.`}
      </Notice>
    )}
  </Flex>
)
